<template>
  <div class="p-city-field p-form-element">
    <p-row>
      <p-col xs2 class="postal-code-column">
        <p-text-field
          v-model="internalLocation.postalCode"
          class="postal-code-field"
          :label="$t('core.address.postalCode')"
          :color="color"
          :text-color="textColor"
          :errors="errors"
          :messages="messages"
          :dense="dense"
          :spacious="spacious"
          :required="required"
          :disabled="disabled"
          autocomplete="postal-code"
          :readonly="readonly"
          @input="onPostalCodeInput"
          @keydown.up.prevent="selectPreviousOption"
          @keydown.down.prevent="selectNextOption"
          @keydown.enter.prevent="onEnter"
          @keydown.shift.tab="onShiftTabDown"
        ></p-text-field>
      </p-col>
      <p-col grow>
        <p-autocomplete
          ref="cityField"
          v-model="internalLocation.city"
          :items="suggestedCities.map((s) => s.data)"
          class="city-field"
          :label="$t('core.address.city')"
          :color="color"
          :text-color="textColor"
          :dense="dense"
          :errors="errors !== undefined ? ' ' : undefined"
          :spacious="spacious"
          :required="required"
          :disabled="disabled"
          :readonly="readonly || isCityInputSuspended"
          :loading="isLoading"
          item-display="city"
          item-detail="stateName"
          autocomplete="address-level2"
          :items-visible.sync="isSuggestionsVisible"
          :open-on-click="false"
          @search="onCityInput"
          @focus="onCityFocus"
          @select-item="selectSuggestion"
        ></p-autocomplete>
      </p-col>
    </p-row>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import deepClone from '../../utils/deepClone';
  import GetLocationsRequestModel from '../../services/v2/model/get-locations-request-model';
  import LocationCitySearchModel from '../../services/v2/model/location-city-search-model';
  import DataRecord from '../../core/v2/data/data-record';
  import _ from '../../utils';
  import type PAutoComplete from '../p-autocomplete/p-autocomplete.vue';

  export default Vue.extend({
    name: 'PCityField',
    props: {
      value: { type: Object, default: () => ({}), required: true },
      color: { type: String, default: undefined },
      textColor: { type: String, default: undefined },
      errors: { type: [Array, String], default: () => undefined },
      messages: { type: [Array, String], default: () => undefined },
      dense: { type: Boolean, default: false },
      spacious: { type: Boolean, default: false },
      required: { type: Boolean, default: false },
      disabled: { type: Boolean, default: false },
      readonly: { type: Boolean, default: false },
    },
    data: () => ({
      isLoading: false,
      isCityInputSuspended: false,
      isSuggestionsVisible: false,
      suggestedCities: [] as DataRecord<LocationCitySearchModel>[],
      internalLocation: {
        postalCode: undefined as string | undefined,
        city: undefined as string | undefined,
        postalCodeAddition: undefined as string | undefined,
        id: undefined as number | undefined,
      } as LocationCitySearchModel,
    }),
    watch: {
      value: {
        async handler(value, oldValue) {
          Vue.set(this, 'internalLocation', deepClone(value));
          if (_.isNil(value.id)) {
            this.$debounce(async () => {
              if (value?.postalCode && value?.postalCode !== oldValue?.postalCode) {
                await this.populateSuggestionsByPostalCode(value.postalCode);
                this.autoSelectMatchedSuggestion(true);
              } else if (value?.city && value?.city !== oldValue?.city) {
                await this.populateSuggestionsByCity(value.city);
                this.autoSelectMatchedSuggestion();
              }
            }, 300, this)();
          }
        },
        deep: true,
        immediate: true,
      },
    },
    methods: {
      getCityField() {
        return this.$refs.cityField as InstanceType<typeof PAutoComplete>;
      },
      onEnter() {
        this.getCityField().onEnterDown();
      },
      selectPreviousOption() {
        this.getCityField().selectPreviousOption();
      },
      selectNextOption() {
        this.getCityField().selectNextOption();
      },
      onShiftTabDown() {
        // Close the items if you tab backwards (shift-modifier), as then the cursor is neither ontop of the postal code nor city field
        this.getCityField().onTabDown();
      },
      selectSuggestion(suggestion: LocationCitySearchModel) {
        if (_.isNil(suggestion)) {
          return;
        }
        this.internalLocation.city = suggestion.city;
        this.internalLocation.postalCode = suggestion.postalCode;
        this.internalLocation.postalCodeAddition = suggestion.postalCodeAddition;
        this.internalLocation.id = suggestion.id;
        this.$emit('input', this.internalLocation);
      },
      clearAdditionalData(data: LocationCitySearchModel) {
        // If any input is done by the user assume it's a custom location
        delete data.id;
        delete data.postalCodeAddition;
      },
      onCityInput(value: string) {
        if (_.isEmpty(value)) {
          this.suggestedCities = [];
          this.$emit('input', this.internalLocation);
          return;
        }
        this.clearAdditionalData(this.internalLocation);
        this.$debounce(async () => {
          await this.populateSuggestionsByCity(value);
          this.autoSelectMatchedSuggestion();
          this.showSuggestionsIfNeeded();
        }, 300, this.onCityInput)();
        this.$emit('input', this.internalLocation);
      },
      async onCityFocus() {
        if (_.isEmpty(this.internalLocation.city)) {
          return;
        }
        if (this.internalLocation.postalCode) {
          await this.populateSuggestionsByPostalCode(this.internalLocation.postalCode);
        }
        this.showSuggestionsIfNeeded();
      },
      onPostalCodeInput(value: string) {
        this.clearAdditionalData(this.internalLocation);
        this.$debounce(async () => {
          this.isCityInputSuspended = true;
          await this.populateSuggestionsByPostalCode(value);
          this.autoSelectMatchedSuggestion(true);
          this.showSuggestionsIfNeeded();
          this.isCityInputSuspended = false;
        }, 300, this.onPostalCodeInput)();
        this.$emit('input', this.internalLocation);
      },
      autoSelectMatchedSuggestion(prioritizePostalCode = false) {
        const areSuggestionPostalCodesAllSame = this.suggestedCities.every((s) => s.data.postalCode === this.suggestedCities[0]?.data.postalCode) && this.suggestedCities.length > 0;
        const samePostalCode = this.internalLocation.postalCode?.trim().toLowerCase() === this.suggestedCities[0]?.data.postalCode?.trim().toLowerCase();
        const sameCity = this.internalLocation.city?.trim().toLowerCase() === this.suggestedCities[0]?.data.city?.trim().toLowerCase();
        const isSameAsInput = (prioritizePostalCode || (sameCity || (prioritizePostalCode && !this.internalLocation.city))) && samePostalCode;
        if (isSameAsInput && areSuggestionPostalCodesAllSame) {
          this.selectSuggestion(this.suggestedCities[0].data);
        }
      },
      showSuggestionsIfNeeded() {
        const alreadySelected = this.internalLocation.id === this.suggestedCities[0]?.data.id;
        this.isSuggestionsVisible = !alreadySelected || (this.suggestedCities.length > 1);
      },
      async populateSuggestionsByPostalCode(search?: string) { return this.populateSuggestions({ postalCode: search }, 2); },
      async populateSuggestionsByCity(search?: string) { return this.populateSuggestions({ cityName: search }, 3); },
      async populateSuggestions(search?: { cityName?: string; postalCode?: string }, minLength = 3) {
        const isSearchValid = Object.values(search ?? {}).some((s) => s === undefined || s.length >= minLength);
        if (!isSearchValid) {
          this.suggestedCities = [];
          return;
        }
        this.isLoading = true;
        const request = new GetLocationsRequestModel({
          searchCriteria: search?.cityName,
          postalCodeCriteria: search?.postalCode,
          'paging.PageSize': 10,
        });
        try {
          const resp = await this.$service.v2.api.core.locations.getLocations(request);
          this.suggestedCities = resp.items ?? [];
        } catch (e) {
          // No feedback for user since it's not critical
          this.suggestedCities = [];
        }
        this.isLoading = false;
      },
    },
  });
</script>
